import { onAuthStateChanged, signOut, type User } from "@firebase/auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, type Me } from "./api";
import { pendingDesktopRequest } from "./desktop";
import { firebaseAuth } from "./firebase";
import { errorMessage } from "./messages";
import { setMonitoringUser } from "./monitoring";

export type Session =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "signed-in"; user: User; me: Me | null; error: string | null };

interface SessionApi {
  session: Session;
  /** Reloads the Firebase user and the account, e.g. after the email was verified. */
  refresh: () => Promise<void>;
  /** A fresh ID token for API calls. */
  token: () => Promise<string>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>({ status: "loading" });
  // Only the latest load may update the session, so a slow earlier answer
  // never overwrites a newer one.
  const latestLoad = useRef(0);

  const loadAccount = useCallback(async (user: User) => {
    const load = ++latestLoad.current;
    let next: Session;
    try {
      next = { status: "signed-in", user, me: await api.me(await user.getIdToken()), error: null };
    } catch (error) {
      next = { status: "signed-in", user, me: null, error: errorMessage(error) };
    }
    if (load === latestLoad.current) {
      setSession(next);
    }
  }, []);

  useEffect(
    () =>
      onAuthStateChanged(firebaseAuth(), (user) => {
        setMonitoringUser(user?.uid ?? null);
        if (user) {
          void loadAccount(user);
        } else {
          latestLoad.current++;
          setSession({ status: "signed-out" });
        }
      }),
    [loadAccount],
  );

  const refresh = useCallback(async () => {
    const user = firebaseAuth().currentUser;
    if (!user) {
      return;
    }
    await user.reload();
    // A new token carries the current email verification state to the API.
    await user.getIdToken(true);
    await loadAccount(user);
  }, [loadAccount]);

  const token = useCallback(async () => {
    const user = firebaseAuth().currentUser;
    if (!user) {
      throw new Error("not signed in");
    }
    return user.getIdToken();
  }, []);

  const value = useMemo<SessionApi>(
    () => ({ session, refresh, token, signOut: () => signOut(firebaseAuth()) }),
    [session, refresh, token],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession outside SessionProvider");
  }
  return value;
}

/** The details and the current terms are in, so the account can be used. */
export function accountComplete(me: Me | null): boolean {
  return Boolean(me && me.profileComplete && me.termsAccepted);
}

/**
 * Where a signed-in customer belongs: details and terms first, then the
 * desktop app if it is waiting for this tab's sign-in, otherwise the account.
 */
export function homeFor(me: Me | null): string {
  if (!accountComplete(me)) {
    return "/complete-profile";
  }
  return pendingDesktopRequest() ? "/desktop" : "/account";
}
